import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HireTalentLayout from "../../layouts/HireTalentLayout";
import Loader from "../../components/loader";
import {
  fetchHireTalentJobApplications,
  rejectHireTalentApplicant,
  scheduleHireTalentInterview,
} from "../../redux/features/hire-talent/actions/authAction";
import TableHeader from "../../components/table/tableHeader";
import { pipViewDate2 } from "../../utils/pip";

const Applicants = () => {
  const dispatch = useDispatch();
  const { subLoading, jobApplications } = useSelector(
    (state) => state.hireTalent.auth,
  );
  const [activeApplicant, setActiveApplicant] = useState(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    date: "",
    time: "",
  });

  useEffect(() => {
    dispatch(fetchHireTalentJobApplications());
  }, [dispatch]);

  const handleScheduleChange = (e) => {
    const { name, value } = e.target;
    setScheduleData((prev) => ({ ...prev, [name]: value }));
  };

  const getTodayDateString = () => new Date().toISOString().split("T")[0];

  const getMinTimeForSelectedDate = () => {
    if (!scheduleData.date) return undefined;
    const today = getTodayDateString();
    if (scheduleData.date !== today) return undefined;
    const minDate = new Date();
    minDate.setMinutes(minDate.getMinutes() + 15);
    const hours = String(minDate.getHours()).padStart(2, "0");
    const minutes = String(minDate.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const formatJobDate = (value) => pipViewDate2(value);

  const handleScheduleSubmit = () => {
    if (!activeApplicant?.applied_id) return;
    if (!scheduleData.date || !scheduleData.time) return;
    const scheduledDateTime = new Date(
      `${scheduleData.date}T${scheduleData.time}:00`,
    ).toISOString();
    dispatch(
      scheduleHireTalentInterview({
        params: activeApplicant.applied_id,
        payload: { scheduled_time: scheduledDateTime },
        callback: (response) => {
          dispatch(fetchHireTalentJobApplications());
          if (response?.success) {
            setIsScheduleModalOpen(false);
            setActiveApplicant(null);
            setScheduleData({ date: "", time: "" });
          }
        },
      }),
    );
  };

  const handleRejectApplicant = () => {
    if (!activeApplicant?.applied_id) return;
    dispatch(
      rejectHireTalentApplicant({
        params: activeApplicant.applied_id,
        payload: { status: "rejected" },
        callback: (response) => {
          if (response?.success) {
            dispatch(fetchHireTalentJobApplications());
            setActiveApplicant(null);
          }
        },
      }),
    );
  };

  return (
    <HireTalentLayout>
      {subLoading && <Loader />}
      <div className="ct_px_40 pb-5 mt-4">
        <div className="ct_mb_20">
          <h4 className="ct_fs_20_new ct_fw_600 ct_darkblue_text">
            Applicants
          </h4>
          <p className="mb-0 ct_grey_text">
            View all freelancers who applied to your jobs
          </p>
        </div>

        <div className="ct_outline_bg ct_box_shadow_card_1 ct_border_radius_10 p-0">
          <div className="table-responsive ct_custom_table ct_custom_table2 pt-2">
            <table className="table">
              <TableHeader
                data={[
                  "#",
                  "Applicant",
                  "Job Title",
                  "Email",
                  "Phone",
                  "Expected Salary",
                  "Work Type",
                  "Status",
                  "Applied At",
                  "Action",
                ]}
              />
              <tbody>
                {jobApplications?.length > 0 &&
                  jobApplications.map((item, index) => (
                    <tr key={item?.applied_id || index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={
                              item?.applicant_profile_image
                                ? `https://api.nu-vio.com/${item?.applicant_profile_image}`
                                : "/assets/img/user_profile.png"
                            }
                            alt=""
                            className="ct_img_30"
                          />
                          <span className="ct_fw_600">
                            {item?.full_name || "#N/A"}
                          </span>
                        </div>
                      </td>
                      <td>{item?.job_title || "#N/A"}</td>
                      <td>{item?.email || "#N/A"}</td>
                      <td>{item?.phone_number || "#N/A"}</td>
                      <td>{item?.expected_salary || "#N/A"}</td>
                      <td>{item?.work_type || "#N/A"}</td>
                      <td className="text-capitalize">
                        {item?.status || "#N/A"}
                      </td>
                      <td>{pipViewDate2(item?.created_at)}</td>
                      <td>
                        <button
                          className="ct_btn_apply_v2"
                          type="button"
                          onClick={() => setActiveApplicant(item)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                {jobApplications?.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center ct_grey_text">
                      No applications found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activeApplicant && (
        <div
          className="modal d-block ct_congratulation_modal_fade"
          tabIndex="-1"
        >
          <div
            className="modal-dialog modal-dialog-centered ct_modal-dialog"
            style={{ maxWidth: "720px" }}
          >
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <h4 className="ct_fs_20 mb-0 ct_darkblue_text">
                    Applicant Details
                  </h4>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn-close ct_circle_close"
                    onClick={() => setActiveApplicant(null)}
                  ></button>
                </div>
              </div>
              <div className="modal-body pt-0">
                <div className="ct_outline_bg ct_border_radius_10 p-3 mt-3 mb-3">
                  <p className="ct_fs_16 mb-2">Job Requirements</p>
                  <ul className="ct_fs_14 mb-0">
                    {activeApplicant?.job_title && (
                      <li className="mb-2">
                        <strong>Job Title:</strong>{" "}
                        <span>{activeApplicant?.job_title}</span>
                      </li>
                    )}
                    {activeApplicant?.job_industry && (
                      <li className="mb-2">
                        <strong>Industry:</strong>{" "}
                        <span>{activeApplicant?.job_industry}</span>
                      </li>
                    )}
                    {activeApplicant?.job_category && (
                      <li className="mb-2">
                        <strong>Category:</strong>{" "}
                        <span>{activeApplicant?.job_category}</span>
                      </li>
                    )}
                    {activeApplicant?.job_location && (
                      <li className="mb-2">
                        <strong>Location:</strong>{" "}
                        <span>{activeApplicant?.job_location}</span>
                      </li>
                    )}
                    {activeApplicant?.job_candidate_location && (
                      <li className="mb-2">
                        <strong>Candidate Location:</strong>{" "}
                        <span>{activeApplicant?.job_candidate_location}</span>
                      </li>
                    )}
                    {activeApplicant?.job_candidate_availability && (
                      <li className="mb-2">
                        <strong>Availability:</strong>{" "}
                        <span>
                          {activeApplicant?.job_candidate_availability}
                        </span>
                      </li>
                    )}
                    {activeApplicant?.job_years_of_experience && (
                      <li className="mb-2">
                        <strong>Years of Experience:</strong>{" "}
                        <span>{activeApplicant?.job_years_of_experience}</span>
                      </li>
                    )}
                    {activeApplicant?.job_minimum_education && (
                      <li className="mb-2">
                        <strong>Minimum Education:</strong>{" "}
                        <span>{activeApplicant?.job_minimum_education}</span>
                      </li>
                    )}
                    {(activeApplicant?.job_min_salary ||
                      activeApplicant?.job_max_salary) && (
                      <li className="mb-2">
                        <strong>Salary:</strong>{" "}
                        <span>
                          {activeApplicant?.job_salary_currency || ""}
                          {activeApplicant?.job_min_salary || "0"} -{" "}
                          {activeApplicant?.job_max_salary || "0"}
                        </span>
                      </li>
                    )}
                    {activeApplicant?.job_start_date && (
                      <li className="mb-2">
                        <strong>Start Date:</strong>{" "}
                        <span>
                          {formatJobDate(activeApplicant?.job_start_date)}
                        </span>
                      </li>
                    )}
                    {activeApplicant?.job_end_date && (
                      <li className="mb-2">
                        <strong>End Date:</strong>{" "}
                        <span>
                          {formatJobDate(activeApplicant?.job_end_date)}
                        </span>
                      </li>
                    )}
                    {activeApplicant?.job_skills && (
                      <li className="mb-2">
                        <strong>Skills:</strong>{" "}
                        <span>{activeApplicant?.job_skills}</span>
                      </li>
                    )}
                    {activeApplicant?.job_about_role && (
                      <li className="mb-2">
                        <strong>About Role:</strong>{" "}
                        <span>{activeApplicant?.job_about_role}</span>
                      </li>
                    )}
                    {activeApplicant?.job_requirement && (
                      <li className="mb-2">
                        <strong>Requirement:</strong>{" "}
                        <span>{activeApplicant?.job_requirement}</span>
                      </li>
                    )}
                    {activeApplicant?.job_job_description && (
                      <li className="mb-0">
                        <strong>Description:</strong>{" "}
                        <span>{activeApplicant?.job_job_description}</span>
                      </li>
                    )}
                  </ul>
                </div>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <img
                    src={
                      activeApplicant?.applicant_profile_image
                        ? `https://api.nu-vio.com/${activeApplicant?.applicant_profile_image}`
                        : "/assets/img/user_profile.png"
                    }
                    alt=""
                    className="ct_img_56"
                  />
                  <div>
                    <h5 className="ct_fs_18 mb-1 ct_darkblue_text">
                      {activeApplicant?.full_name || "#N/A"}
                    </h5>
                    <p className="mb-0 ct_grey_text ct_fs_14">
                      {activeApplicant?.job_title || "#N/A"}
                    </p>
                  </div>
                </div>

                <ul className="ct_fs_14 mb-0">
                  <li className="mb-2">
                    <strong>Email:</strong>{" "}
                    <span>{activeApplicant?.email || "#N/A"}</span>
                  </li>
                  <li className="mb-2">
                    <strong>Phone:</strong>{" "}
                    <span>{activeApplicant?.phone_number || "#N/A"}</span>
                  </li>
                  <li className="mb-2">
                    <strong>Expected Salary:</strong>{" "}
                    <span>{activeApplicant?.expected_salary || "#N/A"}</span>
                  </li>
                  <li className="mb-2">
                    <strong>Work Type:</strong>{" "}
                    <span>{activeApplicant?.work_type || "#N/A"}</span>
                  </li>
                  <li className="mb-2">
                    <strong>Current Position:</strong>{" "}
                    <span>{activeApplicant?.current_position || "#N/A"}</span>
                  </li>
                  <li className="mb-2 d-flex align-items-center gap-2">
                    <strong>LinkedIn:</strong>
                    {activeApplicant?.linkedin_url ? (
                      <>
                        <a
                          href={activeApplicant?.linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate"
                        >
                          {activeApplicant?.linkedin_url}
                        </a>
                      </>
                    ) : (
                      <span>#N/A</span>
                    )}
                  </li>
                  <li className="mb-2 d-flex align-items-center gap-2">
                    <strong>Portfolio:</strong>
                    {activeApplicant?.portfolio_url ? (
                      <>
                        <a
                          href={activeApplicant?.portfolio_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate"
                        >
                          {activeApplicant?.portfolio_url}
                        </a>
                      </>
                    ) : (
                      <span>#N/A</span>
                    )}
                  </li>
                </ul>

                {activeApplicant?.cover_letter && (
                  <div className="mt-3">
                    <p className="ct_fs_16 mb-1">Cover Letter</p>
                    <p className="ct_grey_text mb-0">
                      {activeApplicant.cover_letter}
                    </p>
                  </div>
                )}

                <div className="mt-3">
                  <p className="ct_fs_16 mb-1">Resume</p>
                  {activeApplicant?.resume_url ? (
                    <div className="ct_multiple_img_div ct_custom_scroll d-flex gap-3 mt-2">
                      <div
                        className="position-relative ct_iframe_hover"
                        style={{ width: "120px", height: "120px" }}
                      >
                        <iframe
                          src={activeApplicant.resume_url}
                          title="Resume"
                          loading="lazy"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            border: "0",
                          }}
                        ></iframe>
                        <a
                          href={activeApplicant.resume_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ct_pdf_full_view"
                        >
                          <i className="fa-solid fa-eye ct_cursor_pointer text-white"></i>
                        </a>
                      </div>
                    </div>
                  ) : (
                    <p className="ct_grey_text mb-0">#N/A</p>
                  )}
                </div>
                <div className="d-flex justify-content-end gap-2 mt-4">
                  {!activeApplicant?.scheduled_time ? (
                    <button
                      className="ct_yellow_btn px-4"
                      type="button"
                      style={{ height: "40px", borderRadius: "20px" }}
                      onClick={() => setIsScheduleModalOpen(true)}
                    >
                      Schedule Interview
                    </button>
                  ) : ["accepted", "rejected"].includes(
                      String(
                        activeApplicant?.schedule_status || "",
                      ).toLowerCase(),
                    ) ? (
                    <button
                      className={`ct_yellow_btn px-4 ${
                        String(
                          activeApplicant?.schedule_status || "",
                        ).toLowerCase() === "accepted"
                          ? "ct_sky_blue_btn"
                          : "ct_orange_btn"
                      }`}
                      type="button"
                      style={{ height: "40px", borderRadius: "20px" }}
                      disabled
                    >
                      {String(activeApplicant?.schedule_status || "")}
                    </button>
                  ) : (
                    <button
                      className="ct_yellow_btn ct_sky_blue_btn px-4"
                      type="button"
                      style={{ height: "40px", borderRadius: "20px" }}
                      disabled
                    >
                      Scheduled
                    </button>
                  )}
                  {String(
                    activeApplicant?.schedule_status || "",
                  ).toLowerCase() !== "rejected" && (
                    <button
                      className="ct_yellow_btn ct_orange_btn px-4"
                      type="button"
                      style={{ height: "40px", borderRadius: "20px" }}
                      onClick={handleRejectApplicant}
                    >
                      Reject Applicant
                    </button>
                  )}
                </div>

                <div className="mt-4"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isScheduleModalOpen && (
        <div
          className="modal d-block ct_congratulation_modal_fade"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content ct_border_radius_20">
              <div className="modal-header border-0 align-items-center">
                <h4 className="ct_fs_18 mb-0 ct_darkblue_text">
                  Schedule Interview
                </h4>
                <button
                  type="button"
                  className="btn-close ct_circle_close"
                  onClick={() => setIsScheduleModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body pt-0">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text">Date</label>
                      <input
                        type="date"
                        name="date"
                        value={scheduleData.date}
                        onChange={handleScheduleChange}
                        min={new Date().toISOString().split("T")[0]}
                        className="form-control ct_input_border_8"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="mb-2 ct_fs_14 ct_grey_text">Time</label>
                      <input
                        type="time"
                        name="time"
                        value={scheduleData.time}
                        onChange={handleScheduleChange}
                        min={getMinTimeForSelectedDate()}
                        className="form-control ct_input_border_8"
                      />
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                  <button
                    className="ct_yellow_btn ct_outline_grey px-4"
                    type="button"
                    onClick={() => setIsScheduleModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="ct_yellow_btn px-4"
                    type="button"
                    onClick={handleScheduleSubmit}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </HireTalentLayout>
  );
};

export default Applicants;
